import { ComplexityModel } from './step-models/ComplexityModel';
import { EntryStepModel } from './step-models/EntryStepModel';
import { EXIT_STRATEGY_KEYS, ExitStepModel, pickExitStrategy } from './step-models/ExitStepModel';
import type { ExitStrategySettings } from './step-models/ExitStepModel';
import { SetupStepModel, pickSetupStrategy } from './step-models/SetupStepModel';
import type { SetupStepState, SetupStrategySettings, SetupTopLevelPatch } from './step-models/SetupStepModel';
import { UniverseStepModel } from './step-models/UniverseStepModel';
import type { LocalDate } from '@js-joda/core';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryClient } from '@tanstack/react-query';
import type {
    BacktestComplexityEstimateRequest,
    BacktestSpec,
    BacktestStrategySettings,
    Direction,
    MarketSession,
    NewBacktestSpec,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { Idea, IfThenResearchPlan, ScreenerPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { omit, pick } from 'es-toolkit';
import type { ConditionGroupModel } from 'src/features/searchAlpha/dtos/ConditionGroupModel';
import { conditionGroupModelToIfThenGroup } from 'src/features/searchAlpha/dtos/ConditionGroupModel/conditionGroupModelToIfThenGroup';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { SearchAlphaWidgetModel } from 'src/models/SearchAlphaWidgetModel';
import type { FormulaService } from 'src/services/FormulaService';
import type { ReactBindings } from 'src/types/bindings';

export type BacktestStep = 1 | 2 | 3 | 4 | 5;

/** Steps with their own editable form. Step 5 (Review & Run) is read-only. */
export type EditableBacktestStep = 1 | 2 | 3 | 4;

/** Highest editable step the user has validated (0 means nothing validated yet). */
export type ValidatedThrough = 0 | 1 | 2 | 3 | 4;

const EDITABLE_BACKTEST_STEPS = [1, 2, 3, 4] as const satisfies readonly EditableBacktestStep[];

/**
 * Draft backtest spec — mirrors NewBacktestSpec from the API but with
 * nullable dates to allow incremental form building. strategySettings
 * uses the API type directly; `universePlan` is the screener-style Idea
 * produced by the universe step's `UniverseCreationModel`.
 */
export interface DraftBacktestSpec {
    name: string;
    direction: Direction;
    startDate: LocalDate | null;
    endDate: LocalDate | null;
    marketsToGenerateSignals: MarketSession[];
    universePlan: Idea & { plan: ScreenerPlan };
    strategySettings: BacktestStrategySettings;
}

interface BacktestFormState {
    readonly setup: SetupStepModel;
    readonly universe: UniverseStepModel;
    readonly entry: EntryStepModel;
    readonly exit: ExitStepModel;
    readonly currentStep: BacktestStep;
    readonly visitedSteps: ReadonlySet<number>;
    readonly validatedThrough: ValidatedThrough;
    readonly name: string;
    readonly lastSavedName: string;
}

/**
 * Subset of BacktestSpec that hydrate consumes — no `id` or server metadata,
 * so we can hydrate from either a fetched BacktestSpec or an embedded
 * BacktestJob.spec snapshot (used by the duplicate-from-job flow).
 */
type HydrateBacktestSpec = Pick<
    BacktestSpec,
    | 'name'
    | 'direction'
    | 'startDate'
    | 'endDate'
    | 'marketsToGenerateSignals'
    | 'strategySettings'
    | 'universePlan'
    | 'idea'
>;

type BacktestFormAction =
    // Nav
    | { type: 'GO_TO_STEP'; step: BacktestStep }
    | { type: 'SET_VALIDATED_THROUGH'; step: ValidatedThrough }
    | { type: 'SET_NAME'; name: string }
    | { type: 'LOAD_META'; name: string }
    // Lifecycle (composite — touch multiple sub-models)
    | { type: 'RESET' }
    | { type: 'MARK_SAVED' }
    | { type: 'HYDRATE'; spec: HydrateBacktestSpec };

function clampValidated(step: number): ValidatedThrough {
    if (step <= 0) return 0;
    if (step >= 4) return 4;
    return step as ValidatedThrough;
}

function formReducer(state: BacktestFormState, action: BacktestFormAction): BacktestFormState {
    switch (action.type) {
        // Nav
        case 'GO_TO_STEP': {
            const visitedSteps = state.visitedSteps.has(state.currentStep)
                ? state.visitedSteps
                : new Set([...state.visitedSteps, state.currentStep]);
            const validatedThrough = clampValidated(Math.max(state.validatedThrough, action.step - 1));
            return { ...state, currentStep: action.step, visitedSteps, validatedThrough };
        }
        case 'SET_VALIDATED_THROUGH':
            return { ...state, validatedThrough: action.step };
        case 'SET_NAME':
            return { ...state, name: action.name };
        case 'LOAD_META':
            return {
                ...state,
                name: action.name,
                lastSavedName: action.name,
                currentStep: 1,
                visitedSteps: new Set(),
                validatedThrough: 0,
            };

        // Lifecycle
        case 'RESET':
            state.setup.reset();
            state.universe.reset();
            state.entry.reset();
            state.exit.reset();
            return {
                ...state,
                currentStep: 1,
                visitedSteps: new Set(),
                validatedThrough: 0,
                name: '',
                lastSavedName: '',
            };
        case 'MARK_SAVED':
            state.setup.markSaved();
            state.universe.markSaved();
            state.entry.markSaved();
            state.exit.markSaved();
            return { ...state, lastSavedName: state.name };
        case 'HYDRATE': {
            state.setup.hydrate({
                direction: action.spec.direction,
                startDate: action.spec.startDate,
                endDate: action.spec.endDate,
                marketsToGenerateSignals: action.spec.marketsToGenerateSignals,
                strategy: pickSetupStrategy(action.spec.strategySettings),
            } satisfies SetupStepState);
            state.universe.hydrate(action.spec.universePlan);
            state.exit.hydrate({
                strategy: pickExitStrategy(action.spec.strategySettings),
            });
            if (action.spec.idea != null) {
                void state.entry.hydrate(action.spec.idea);
            }
            return {
                ...state,
                name: action.spec.name,
                lastSavedName: action.spec.name,
                currentStep: 1,
                visitedSteps: new Set(),
                validatedThrough: 0,
            };
        }

        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Form-level model for the Backtest widget. Owns the four editable sub-forms
 * (Setup, Universe, Entry, Exit), wizard navigation, and the backtest name.
 *
 * Execution state (jobs, results, the in-flight run mutation) lives on the
 * widget impl; the form is purely the editable spec.
 */
export class BacktestFormModel implements Disposable {
    readonly #disposableStack = new DisposableStack();
    readonly #state: Signal<BacktestFormState>;

    // Wizard navigation + name
    readonly currentStep: ReadonlySignal<BacktestStep>;
    readonly visitedSteps: ReadonlySignal<ReadonlySet<number>>;
    readonly validatedThrough: ReadonlySignal<ValidatedThrough>;
    readonly name: ReadonlySignal<string>;

    // Per-step signals re-exposed at the form level
    readonly entryConditions: ReadonlySignal<ConditionGroupModel[]>;
    readonly entryFormula: ReadonlySignal<string | null>;
    readonly entryImports: ReadonlySignal<IndicatorImportRef[]>;

    // Composed
    readonly draft: ReadonlySignal<DraftBacktestSpec>;
    readonly currentStepErrors: ReadonlySignal<readonly string[]>;
    readonly validationErrors: ReadonlySignal<string[]>;
    readonly completedSteps: ReadonlySignal<ReadonlySet<number>>;
    readonly canAdvance: ReadonlySignal<boolean>;
    readonly canRun: ReadonlySignal<boolean>;
    readonly isDirty: ReadonlySignal<boolean>;

    readonly complexity: ComplexityModel;

    /** Validation errors per editable step, keyed by step number (1=Setup, 2=Universe, 3=Entry, 4=Exit). */
    readonly stepValidationErrors: ReadonlySignal<Readonly<Record<EditableBacktestStep, readonly string[]>>>;

    get #setup(): SetupStepModel {
        return this.#state.peek().setup;
    }
    get setupStep(): SetupStepModel {
        return this.#state.peek().setup;
    }
    get #universe(): UniverseStepModel {
        return this.#state.peek().universe;
    }
    get #entry(): EntryStepModel {
        return this.#state.peek().entry;
    }
    get #exit(): ExitStepModel {
        return this.#state.peek().exit;
    }
    get exitStep(): ExitStepModel {
        return this.#state.peek().exit;
    }

    constructor(logger: ReactBindings['Logger'], queryClient: QueryClient, formulaService: FormulaService) {
        const setup = new SetupStepModel();
        const universe = new UniverseStepModel(queryClient, logger, setup.startDate, setup.endDate, formulaService);
        const entry = new EntryStepModel(logger);
        const exit = new ExitStepModel();
        this.#disposableStack.use(entry);
        this.#disposableStack.use(universe);

        this.#state = signal<BacktestFormState>({
            setup,
            universe,
            entry,
            exit,
            currentStep: 1,
            visitedSteps: new Set(),
            validatedThrough: 0,
            name: '',
            lastSavedName: '',
        });

        this.currentStep = computed(() => this.#state.value.currentStep);
        this.visitedSteps = computed(() => this.#state.value.visitedSteps);
        this.validatedThrough = computed(() => this.#state.value.validatedThrough);
        this.name = computed(() => this.#state.value.name);

        this.entryConditions = entry.conditions;
        this.entryFormula = entry.formula;
        this.entryImports = entry.imports;

        this.draft = computed(() => {
            const setupState = setup.state.value;
            return {
                name: this.#state.value.name,
                direction: setupState.direction,
                startDate: setupState.startDate,
                endDate: setupState.endDate,
                marketsToGenerateSignals: setupState.marketsToGenerateSignals,
                universePlan: universe.creationModel.idea.value,
                strategySettings: {
                    ...setupState.strategy,
                    ...exit.state.value.strategy,
                },
            };
        });

        this.stepValidationErrors = computed(() => ({
            1: setup.validationErrors.value,
            2: universe.errors.value,
            3: entry.validationErrors.value,
            4: exit.validationErrors.value,
        }));

        this.currentStepErrors = computed(() => {
            const step = this.#state.value.currentStep;
            // Step 5 (Run) is a results view, not a form step — no errors to surface.
            if (step === 5) return [];
            return this.stepValidationErrors.value[step];
        });

        this.validationErrors = computed(() => {
            const { currentStep, validatedThrough } = this.#state.value;
            if (currentStep > validatedThrough) return [];
            return [...this.currentStepErrors.value];
        });

        this.completedSteps = computed(() => {
            const completed = new Set<number>();
            const errors = this.stepValidationErrors.value;
            for (const step of EDITABLE_BACKTEST_STEPS) {
                if (errors[step].length === 0) completed.add(step);
            }
            return completed;
        });

        this.canAdvance = computed(() => {
            const step = this.#state.value.currentStep;
            // Step 5 (Review & Run) has no Next button; default to true.
            if (step === 5) return true;
            return this.stepValidationErrors.value[step].length === 0;
        });

        // Fires the complexity-estimate preflight whenever every step is
        // structurally valid (i.e. the draft can serialize as `NewBacktestSpec`).
        // Returns null otherwise so `getBacktestComplexityEstimateQuery` skips
        // the call — and so we sidestep the chicken-and-egg where complexity
        // gates `canRun` and `canRun` would otherwise gate the request.
        const complexityRequest = computed<BacktestComplexityEstimateRequest | null>(() => {
            if (!setup.isValid.value || !universe.isValid.value || !entry.isValid.value || !exit.isValid.value) {
                return null;
            }
            const setupState = setup.state.value;
            if (setupState.startDate == null || setupState.endDate == null) return null;
            // Subscribe to entry edits — `formula` reads the entry step's
            // `#conditionsVersion` counter, so this re-evaluates whenever the
            // condition tree mutates underneath the (non-reactive) search model.
            void entry.formula.value;
            void entry.imports.value;
            const spec = {
                name: this.#state.peek().name,
                idea: this.#getIdeaRef(),
                universePlan: universe.creationModel.idea.value,
                direction: setupState.direction,
                startDate: setupState.startDate,
                endDate: setupState.endDate,
                marketsToGenerateSignals: setupState.marketsToGenerateSignals,
                strategySettings: {
                    ...setupState.strategy,
                    ...exit.state.value.strategy,
                },
            } satisfies NewBacktestSpec;
            return { spec };
        });

        const complexity = new ComplexityModel({ queryClient, request: complexityRequest });
        this.#disposableStack.use(complexity);
        this.complexity = complexity;

        this.canRun = computed(
            () =>
                setup.isValid.value &&
                universe.isValid.value &&
                entry.isValid.value &&
                exit.isValid.value &&
                !complexity.isBlocking.value,
        );

        this.isDirty = computed(
            () =>
                this.#state.value.name !== this.#state.value.lastSavedName ||
                setup.isDirty.value ||
                universe.isDirty.value ||
                entry.isDirty.value ||
                exit.isDirty.value,
        );
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }

    #dispatch(action: BacktestFormAction): void {
        this.#state.value = formReducer(this.#state.value, action);
    }

    get entrySearchModel(): SearchAlphaWidgetModel | null {
        return this.#entry.searchModel;
    }

    setEntrySearchModel(model: SearchAlphaWidgetModel): void {
        this.#entry.setSearchModel(model);
    }

    /** Replace the entry conditions with a quick-start preset's if-then plan. */
    applyEntryQuickStart(plan: IfThenResearchPlan): void {
        void this.#entry.applyQuickStart(plan);
    }

    goToStep(step: BacktestStep): void {
        this.#dispatch({ type: 'GO_TO_STEP', step });
    }

    nextStep(): void {
        const current = this.currentStep.peek();
        if (current >= 5) return;
        const validatedThrough = clampValidated(Math.max(this.#state.peek().validatedThrough, current));
        this.#dispatch({ type: 'SET_VALIDATED_THROUGH', step: validatedThrough });
        if (this.currentStepErrors.peek().length > 0) return;
        this.#dispatch({ type: 'GO_TO_STEP', step: (current + 1) as BacktestStep });
    }

    previousStep(): void {
        const current = this.currentStep.peek();
        if (current <= 1) return;
        this.#dispatch({ type: 'GO_TO_STEP', step: (current - 1) as BacktestStep });
    }

    markAllStepsValidated(): void {
        this.#dispatch({ type: 'SET_VALIDATED_THROUGH', step: 4 });
    }

    clearValidation(): void {
        this.#dispatch({ type: 'SET_VALIDATED_THROUGH', step: 0 });
    }

    setName(name: string): void {
        this.#dispatch({ type: 'SET_NAME', name });
    }

    patchDraft(partial: Partial<DraftBacktestSpec>): void {
        const setupPatch: SetupTopLevelPatch = {};
        if ('direction' in partial) setupPatch.direction = partial.direction;
        if ('startDate' in partial) setupPatch.startDate = partial.startDate ?? null;
        if ('endDate' in partial) setupPatch.endDate = partial.endDate ?? null;
        if ('marketsToGenerateSignals' in partial && partial.marketsToGenerateSignals != null) {
            setupPatch.marketsToGenerateSignals = partial.marketsToGenerateSignals;
        }
        if (Object.keys(setupPatch).length > 0) {
            this.#setup.patchFields(setupPatch);
        }
        if ('name' in partial && partial.name != null) {
            this.#dispatch({ type: 'SET_NAME', name: partial.name });
        }
    }

    patchStrategySettings(partial: Partial<BacktestStrategySettings>): void {
        const exitPartial = pick(partial, EXIT_STRATEGY_KEYS);
        const setupPartial = omit(partial, EXIT_STRATEGY_KEYS);
        if (Object.keys(setupPartial).length > 0) {
            this.#setup.patchStrategy(setupPartial as Partial<SetupStrategySettings>);
        }
        if (Object.keys(exitPartial).length > 0) {
            this.#exit.patchStrategy(exitPartial as Partial<ExitStrategySettings>);
        }
    }

    get universe(): UniverseStepModel {
        return this.#universe;
    }

    hydrate(spec: HydrateBacktestSpec): void {
        this.#dispatch({ type: 'HYDRATE', spec });
    }

    reset(): void {
        this.#dispatch({ type: 'RESET' });
    }

    markSaved(): void {
        this.#dispatch({ type: 'MARK_SAVED' });
    }

    buildNewSpec(): NewBacktestSpec {
        const setup = this.#setup.state.value;
        if (setup.startDate == null || setup.endDate == null) {
            throw new Error('Cannot build spec: startDate and endDate are required');
        }
        const strategySettings: BacktestStrategySettings = {
            ...setup.strategy,
            ...this.#exit.state.value.strategy,
        };
        return {
            name: this.name.peek(),
            idea: this.#getIdeaRef(),
            universePlan: this.#universe.creationModel.idea.peek(),
            direction: setup.direction,
            startDate: setup.startDate,
            endDate: setup.endDate,
            marketsToGenerateSignals: setup.marketsToGenerateSignals,
            strategySettings,
        };
    }

    #getIdeaRef(): NewBacktestSpec['idea'] {
        const searchModel = this.#entry.searchModel;
        if (!searchModel) return '00000000-0000-0000-0000-000000000000';

        try {
            const root = conditionGroupModelToIfThenGroup(searchModel.rootGroup);
            const imports = this.#entry.imports.value;
            return {
                isTemplate: false,
                name: null,
                key: 'ticker',
                description: '',
                defaultUniverseId: null,
                defaultColumnTemplateId: null,
                plan: {
                    type: 'if-then' as const,
                    root,
                    imports,
                },
            };
        } catch {
            return '00000000-0000-0000-0000-000000000000';
        }
    }
}
